(function () {
  'use strict';

  const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
  const HANDLE_RADIUS = 24;
  const MESH_SIZE = 8;
  const DEFAULT_OCCLUSION_BRUSH_RADIUS = 28;
  const MIN_OCCLUSION_BRUSH_RADIUS = 12;
  const MAX_OCCLUSION_BRUSH_RADIUS = 72;
  const ROOM_ANALYSIS_MAX_EDGE = 560;
  const FLOOR_FORESHORTENING = 0.58;
  const ROOM_ANALYSIS_MODULE =
    'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.8.1';
  const ROOM_ANALYSIS_MODEL = 'onnx-community/depth-anything-v2-small';
  let roomEstimatorPromise = null;
  let roomEstimatorBackend = 'wasm';

  function getRoomEstimator() {
    if (!roomEstimatorPromise) {
      roomEstimatorPromise = import(ROOM_ANALYSIS_MODULE).then(async function (transformers) {
        if ('gpu' in navigator) {
          try {
            roomEstimatorBackend = 'webgpu';
            return await transformers.pipeline('depth-estimation', ROOM_ANALYSIS_MODEL, {
              device: 'webgpu',
              dtype: 'q4',
            });
          } catch (error) {
            roomEstimatorBackend = 'wasm';
          }
        }
        return transformers.pipeline('depth-estimation', ROOM_ANALYSIS_MODEL, {
          device: 'wasm',
          dtype: 'q4',
        });
      });
    }
    return roomEstimatorPromise;
  }

  async function estimateRoomDepth(input) {
    const estimator = await getRoomEstimator();
    try {
      return await estimator(input);
    } catch (error) {
      if (roomEstimatorBackend !== 'webgpu') throw error;
      if (estimator.dispose) await estimator.dispose();
      roomEstimatorBackend = 'wasm';
      roomEstimatorPromise = import(ROOM_ANALYSIS_MODULE).then(function (transformers) {
        return transformers.pipeline('depth-estimation', ROOM_ANALYSIS_MODEL, {
          device: 'wasm',
          dtype: 'q4',
        });
      });
      return (await roomEstimatorPromise)(input);
    }
  }

  function warmRoomEstimator() {
    const load = function () {
      getRoomEstimator().catch(function () {
        roomEstimatorPromise = null;
      });
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 1200 });
    else window.setTimeout(load, 0);
  }

  function smoothstep(minimum, maximum, value) {
    const normalized = Math.max(0, Math.min(1, (value - minimum) / (maximum - minimum)));
    return normalized * normalized * (3 - 2 * normalized);
  }

  function refineOcclusionScores(scores, guide, width, height) {
    let current = scores;
    for (let pass = 0; pass < 2; pass += 1) {
      const refined = new Float32Array(scores.length);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixel = y * width + x;
          const guideOffset = pixel * 4;
          let total = current[pixel] * 3;
          let totalWeight = 3;
          for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
            const neighborY = y + offsetY;
            if (neighborY < 0 || neighborY >= height) continue;
            for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
              if (!offsetX && !offsetY) continue;
              const neighborX = x + offsetX;
              if (neighborX < 0 || neighborX >= width) continue;
              const neighbor = neighborY * width + neighborX;
              const neighborOffset = neighbor * 4;
              const colorDifference =
                (Math.abs(guide[guideOffset] - guide[neighborOffset]) +
                  Math.abs(guide[guideOffset + 1] - guide[neighborOffset + 1]) +
                  Math.abs(guide[guideOffset + 2] - guide[neighborOffset + 2])) /
                3;
              const edgeWeight = 1 / (1 + (colorDifference * colorDifference) / 625);
              const spatialWeight = offsetX && offsetY ? 0.7 : 1;
              const weight = edgeWeight * spatialWeight;
              total += current[neighbor] * weight;
              totalWeight += weight;
            }
          }
          refined[pixel] = total / totalWeight;
        }
      }
      current = refined;
    }
    return current;
  }

  function removeMaskSpeckles(scores, width, height) {
    const labels = new Int32Array(scores.length);
    const queue = new Int32Array(scores.length);
    const areas = [0];
    let label = 0;
    for (let start = 0; start < scores.length; start += 1) {
      if (scores[start] < 0.48 || labels[start]) continue;
      label += 1;
      let head = 0;
      let tail = 0;
      queue[tail++] = start;
      labels[start] = label;
      while (head < tail) {
        const pixel = queue[head++];
        const x = pixel % width;
        const y = Math.floor(pixel / width);
        const neighbors = [pixel - 1, pixel + 1, pixel - width, pixel + width];
        for (let index = 0; index < neighbors.length; index += 1) {
          const neighbor = neighbors[index];
          if (neighbor < 0 || neighbor >= scores.length || labels[neighbor]) continue;
          if ((index === 0 && x === 0) || (index === 1 && x === width - 1)) continue;
          if ((index === 2 && y === 0) || (index === 3 && y === height - 1)) continue;
          if (scores[neighbor] < 0.48) continue;
          labels[neighbor] = label;
          queue[tail++] = neighbor;
        }
      }
      areas[label] = tail;
    }
    const minimumArea = Math.max(12, Math.round(scores.length * 0.00005));
    for (let pixel = 0; pixel < labels.length; pixel += 1) {
      if (labels[pixel] && areas[labels[pixel]] < minimumArea) labels[pixel] = 0;
    }
    return labels;
  }

  function estimateFloorBoundary(floorDepth, guide, width) {
    const height = floorDepth.length;
    const fitStart = Math.round(height * 0.62);
    const fitEnd = Math.max(fitStart + 1, Math.round(height * 0.94));
    let sumY = 0;
    let sumDepth = 0;
    let sumYY = 0;
    let sumYDepth = 0;
    let count = 0;
    for (let y = fitStart; y <= fitEnd; y += 1) {
      const normalizedY = y / Math.max(1, height - 1);
      const value = floorDepth[y];
      sumY += normalizedY;
      sumDepth += value;
      sumYY += normalizedY * normalizedY;
      sumYDepth += normalizedY * value;
      count += 1;
    }
    const denominator = count * sumYY - sumY * sumY;
    let depthBoundary = Math.round(height * 0.5);
    if (Math.abs(denominator) >= 0.0001) {
      const slope = (count * sumYDepth - sumY * sumDepth) / denominator;
      const intercept = (sumDepth - slope * sumY) / count;
      if (slope >= 4) {
        const tolerance = Math.max(2.5, Math.abs(slope) * 0.055);
        const windowSize = Math.max(6, Math.round(height * 0.07));
        const searchStart = Math.round(height * 0.34);
        const searchEnd = Math.round(height * 0.68);
        for (let y = searchStart; y <= searchEnd; y += 1) {
          let matches = 0;
          for (let offset = 0; offset < windowSize && y + offset < height; offset += 1) {
            const row = y + offset;
            const prediction = intercept + slope * (row / Math.max(1, height - 1));
            if (Math.abs(floorDepth[row] - prediction) <= tolerance) matches += 1;
          }
          if (matches / windowSize >= 0.72) {
            depthBoundary = y;
            break;
          }
        }
      }
    }
    const edgeScores = new Float32Array(height);
    const colorSearchStart = Math.round(height * 0.48);
    const colorSearchEnd = Math.round(height * 0.72);
    for (let y = colorSearchStart; y <= colorSearchEnd; y += 1) {
      const upperY = Math.max(0, y - 2);
      const lowerY = Math.min(height - 1, y + 2);
      let score = 0;
      let samples = 0;
      for (let x = Math.round(width * 0.05); x < width * 0.95; x += 4) {
        const upper = (upperY * width + x) * 4;
        const lower = (lowerY * width + x) * 4;
        score +=
          Math.abs(guide[upper] - guide[lower]) +
          Math.abs(guide[upper + 1] - guide[lower + 1]) +
          Math.abs(guide[upper + 2] - guide[lower + 2]);
        samples += 1;
      }
      edgeScores[y] = score / Math.max(1, samples);
    }
    let colorBoundary = Math.round(height * 0.54);
    let strongestEdge = -1;
    for (let y = colorSearchStart; y <= colorSearchEnd; y += 1) {
      let smoothedEdge = 0;
      let samples = 0;
      for (let offset = -3; offset <= 3; offset += 1) {
        const row = Math.max(0, Math.min(height - 1, y + offset));
        smoothedEdge += edgeScores[row];
        samples += 1;
      }
      smoothedEdge /= samples;
      if (smoothedEdge > strongestEdge) {
        strongestEdge = smoothedEdge;
        colorBoundary = y;
      }
    }
    const safeBoundary = Math.max(depthBoundary, colorBoundary) + height * 0.012;
    return Math.round(Math.max(height * 0.54, Math.min(height * 0.7, safeBoundary)));
  }
  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function centerOf(points) {
    return points.reduce(
      function (center, point) {
        center.x += point.x / points.length;
        center.y += point.y / points.length;
        return center;
      },
      { x: 0, y: 0 },
    );
  }

  function pointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const a = polygon[i];
      const b = polygon[j];
      const intersects =
        a.y > point.y !== b.y > point.y &&
        point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || 0.0001) + a.x;
      if (intersects) inside = !inside;
    }
    return inside;
  }

  function solveLinearSystem(matrix, values) {
    const size = values.length;
    const rows = matrix.map(function (row, index) {
      return row.concat(values[index]);
    });
    for (let column = 0; column < size; column += 1) {
      let pivot = column;
      for (let row = column + 1; row < size; row += 1) {
        if (Math.abs(rows[row][column]) > Math.abs(rows[pivot][column])) pivot = row;
      }
      if (Math.abs(rows[pivot][column]) < 1e-9) return null;
      [rows[column], rows[pivot]] = [rows[pivot], rows[column]];
      const divisor = rows[column][column];
      for (let item = column; item <= size; item += 1) rows[column][item] /= divisor;
      for (let row = 0; row < size; row += 1) {
        if (row === column) continue;
        const factor = rows[row][column];
        for (let item = column; item <= size; item += 1) {
          rows[row][item] -= factor * rows[column][item];
        }
      }
    }
    return rows.map(function (row) {
      return row[size];
    });
  }

  function homographyFor(corners) {
    const source = [
      { u: 0, v: 0 },
      { u: 1, v: 0 },
      { u: 1, v: 1 },
      { u: 0, v: 1 },
    ];
    const matrix = [];
    const values = [];
    source.forEach(function (sourcePoint, index) {
      const destination = corners[index];
      const u = sourcePoint.u;
      const v = sourcePoint.v;
      matrix.push([u, v, 1, 0, 0, 0, -u * destination.x, -v * destination.x]);
      values.push(destination.x);
      matrix.push([0, 0, 0, u, v, 1, -u * destination.y, -v * destination.y]);
      values.push(destination.y);
    });
    return solveLinearSystem(matrix, values);
  }

  function projectPoint(homography, u, v) {
    const denominator = homography[6] * u + homography[7] * v + 1;
    return {
      x: (homography[0] * u + homography[1] * v + homography[2]) / denominator,
      y: (homography[3] * u + homography[4] * v + homography[5]) / denominator,
    };
  }

  function drawTriangle(context, image, source, destination) {
    const x0 = source[0].x;
    const y0 = source[0].y;
    const x1 = source[1].x;
    const y1 = source[1].y;
    const x2 = source[2].x;
    const y2 = source[2].y;
    const denominator = x0 * (y1 - y2) + x1 * (y2 - y0) + x2 * (y0 - y1);
    if (Math.abs(denominator) < 0.0001) return;
    const a =
      (destination[0].x * (y1 - y2) +
        destination[1].x * (y2 - y0) +
        destination[2].x * (y0 - y1)) /
      denominator;
    const b =
      (destination[0].y * (y1 - y2) +
        destination[1].y * (y2 - y0) +
        destination[2].y * (y0 - y1)) /
      denominator;
    const c =
      (destination[0].x * (x2 - x1) +
        destination[1].x * (x0 - x2) +
        destination[2].x * (x1 - x0)) /
      denominator;
    const d =
      (destination[0].y * (x2 - x1) +
        destination[1].y * (x0 - x2) +
        destination[2].y * (x1 - x0)) /
      denominator;
    const e =
      (destination[0].x * (x1 * y2 - x2 * y1) +
        destination[1].x * (x2 * y0 - x0 * y2) +
        destination[2].x * (x0 * y1 - x1 * y0)) /
      denominator;
    const f =
      (destination[0].y * (x1 * y2 - x2 * y1) +
        destination[1].y * (x2 * y0 - x0 * y2) +
        destination[2].y * (x0 * y1 - x1 * y0)) /
      denominator;

    context.save();
    context.beginPath();
    context.moveTo(destination[0].x, destination[0].y);
    context.lineTo(destination[1].x, destination[1].y);
    context.lineTo(destination[2].x, destination[2].y);
    context.closePath();
    context.clip();
    context.transform(a, b, c, d, e, f);
    context.drawImage(image, 0, 0);
    context.restore();
  }

  class RugVisualizer {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-visualizer-dialog]');
      this.stage = root.querySelector('[data-visualizer-stage]');
      this.canvas = root.querySelector('[data-visualizer-canvas]');
      this.context = this.canvas.getContext('2d', { alpha: false });
      this.empty = root.querySelector('[data-visualizer-empty]');
      this.status = root.querySelector('[data-visualizer-status]');
      this.sizeSelect = root.querySelector('[data-visualizer-size]');
      this.occlusionToggle = root.querySelector('[data-visualizer-occlusion-toggle]');
      this.occlusionTools = root.querySelector('[data-visualizer-occlusion-tools]');
      this.brushSizeControl = root.querySelector('[data-visualizer-occlusion-brush]');
      this.brushSizeOutput = root.querySelector('[data-visualizer-occlusion-brush-value]');
      this.shape = root.dataset.shape === 'round' ? 'round' : 'rectangle';
      this.sectionId = root.dataset.sectionId;
      this.selectedVariantId = String(root.dataset.selectedVariantId || '');
      this.data = JSON.parse(root.querySelector('[data-visualizer-data]').textContent);
      this.variants = this.data.variants.filter(function (variant) {
        return variant.available;
      });
      this.photo = null;
      this.rugImage = null;
      this.rugTexture = null;
      this.corners = null;
      this.viewport = { width: 0, height: 0 };
      this.pointers = new Map();
      this.drag = null;
      this.gesture = null;
      this.frameRequested = false;
      this.previousOverflow = '';
      this.lastFocus = null;
      this.photoZoom = 1;
      this.rugOpacity = 1;
      this.rugBrightness = 1;
      this.shadowStrength = 0.65;
      this.occlusionMode = false;
      this.occlusionAction = 'hide';
      this.brushRadius = DEFAULT_OCCLUSION_BRUSH_RADIUS;
      this.occlusionStrokes = [];
      this.brushPoint = null;
      this.automaticOcclusionMask = null;
      this.floorBoundary = null;
      this.occlusionDirty = true;
      this.analysisSequence = 0;
      this.occlusionMaskCanvas = document.createElement('canvas');
      this.occlusionForegroundCanvas = document.createElement('canvas');
      this.history = [];
      this.historyIndex = -1;
      this.interactionChanged = false;
      this.bind();
    }

    bind() {
      this.root.querySelector('[data-visualizer-open]').addEventListener('click', () => this.open());
      this.root.querySelector('[data-visualizer-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.root.querySelectorAll('[data-visualizer-file], [data-visualizer-camera]').forEach((input) => {
        input.addEventListener('change', (event) => this.readPhoto(event.target.files[0]));
      });
      this.sizeSelect.addEventListener('change', () => {
        this.updateRatioForVariant(this.sizeSelect.value, true);
        this.emit('size_selected');
      });
      this.root.querySelector('[data-visualizer-reset]').addEventListener('click', () => this.reset(true));
      this.root.querySelectorAll('[data-visualizer-rotate]').forEach((button) => {
        button.addEventListener('click', () => this.rotate(Number(button.dataset.visualizerRotate), true));
      });
      this.root.querySelector('[data-visualizer-undo]').addEventListener('click', () => this.undo());
      this.root.querySelector('[data-visualizer-redo]').addEventListener('click', () => this.redo());
      this.occlusionToggle.addEventListener('click', () => this.setOcclusionMode(true));
      this.root.querySelector('[data-visualizer-occlusion-done]').addEventListener('click', () =>
        this.setOcclusionMode(false),
      );
      this.root.querySelectorAll('[data-visualizer-occlusion-action]').forEach((button) => {
        button.addEventListener('click', () => this.setOcclusionAction(button.dataset.visualizerOcclusionAction));
      });
      this.brushSizeControl.addEventListener('input', () => {
        this.brushRadius = Math.min(
          MAX_OCCLUSION_BRUSH_RADIUS,
          Math.max(MIN_OCCLUSION_BRUSH_RADIUS, Number(this.brushSizeControl.value)),
        );
        this.brushSizeOutput.textContent = `${this.brushRadius} px`;
        this.requestRender();
      });
      this.root.querySelectorAll('[data-visualizer-control]').forEach((control) => {
        const property = control.dataset.visualizerControl;
        control.addEventListener('input', () => {
          this[property] = Number(control.value) / 100;
          if (property === 'photoZoom') this.occlusionDirty = true;
          this.requestRender();
        });
        control.addEventListener('change', () => this.commitHistory());
      });
      this.root.querySelector('[data-visualizer-confirm]').addEventListener('click', () => this.confirm());
      this.canvas.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.canvas.addEventListener('pointermove', (event) => this.pointerMove(event));
      this.canvas.addEventListener('pointerup', (event) => this.pointerUp(event));
      this.canvas.addEventListener('pointercancel', (event) => this.pointerUp(event));
      this.canvas.addEventListener('pointerleave', () => {
        if (!this.pointers.size) {
          this.brushPoint = null;
          this.requestRender();
        }
      });
      this.canvas.addEventListener(
        'wheel',
        (event) => {
          if (!this.photo || !this.corners || this.occlusionMode) return;
          event.preventDefault();
          this.scale(event.deltaY < 0 ? 1.05 : 0.95, true);
        },
        { passive: false },
      );
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.dialog.hidden) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      window.addEventListener('variantMediaUpdate', (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant && variant.id) this.selectVariant(String(variant.id), false);
      });
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.stage);
      this.syncControlValues();
      this.updateHistoryButtons();
    }

    open() {
      this.lastFocus = document.activeElement;
      this.dialog.hidden = false;
      if (this.dialog.showModal && !this.dialog.open) this.dialog.showModal();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.root.querySelector('[data-visualizer-close]').focus();
      this.emit('opened');
      warmRoomEstimator();
      requestAnimationFrame(() => {
        this.resize();
        this.loadRug();
      });
    }

    close() {
      this.setOcclusionMode(false);
      if (this.dialog.open) this.dialog.close();
      this.dialog.hidden = true;
      document.documentElement.style.overflow = this.previousOverflow;
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || this.dialog.hidden) return;
      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), select:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async loadRug() {
      if (this.rugImage || this.rugLoading) return;
      this.rugLoading = true;
      this.setLoading(true);
      try {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.src = this.root.dataset.imageUrl;
        await image.decode();
        this.rugImage = image;
        this.rugTexture = this.prepareRugTexture(image);
        if (!this.corners) this.reset(false);
        this.requestRender();
      } catch (error) {
        this.status.textContent = this.root.dataset.rugError;
      } finally {
        this.rugLoading = false;
        this.setLoading(false);
      }
    }

    prepareRugTexture(image) {
      if (this.shape !== 'round') return image;
      const texture = document.createElement('canvas');
      texture.width = image.naturalWidth;
      texture.height = image.naturalHeight;
      const context = texture.getContext('2d');
      context.beginPath();
      context.ellipse(
        texture.width / 2,
        texture.height / 2,
        texture.width / 2,
        texture.height / 2,
        0,
        0,
        Math.PI * 2,
      );
      context.clip();
      context.drawImage(image, 0, 0);
      return texture;
    }

    async readPhoto(file) {
      this.status.textContent = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.status.textContent = this.root.dataset.photoTypeError;
        return;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        this.status.textContent = this.root.dataset.photoSizeError;
        return;
      }
      this.setLoading(true);
      const analysisSequence = ++this.analysisSequence;
      try {
        let image;
        if ('createImageBitmap' in window) {
          image = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } else {
          image = await this.loadLocalImage(file);
        }
        if (this.photo && this.photo.close) this.photo.close();
        this.photo = image;
        this.automaticOcclusionMask = null;
        this.floorBoundary = null;
        this.occlusionDirty = true;
        this.occlusionToggle.disabled = false;
        this.empty.hidden = true;
        this.history = [];
        this.historyIndex = -1;
        this.reset(false);
        this.commitHistory();
        this.requestRender();
        this.emit('photo_selected');
        this.status.textContent = this.root.dataset.roomAnalysis;
        const placementBeforeAnalysis = this.corners
          ? this.corners.map(function (point) {
              return { x: point.x, y: point.y };
            })
          : null;
        try {
          const mask = await this.createAutomaticOcclusionMask();
          if (analysisSequence !== this.analysisSequence) return;
          this.automaticOcclusionMask = mask;
          this.floorBoundary = mask.floorBoundary;
          this.occlusionDirty = true;
          const placementUnchanged =
            placementBeforeAnalysis &&
            this.corners &&
            placementBeforeAnalysis.every(
              (point, index) => distance(point, this.corners[index]) < 0.5,
            );
          if (placementUnchanged) this.fitRugToFloor(mask.floorBoundary);
          this.status.textContent = this.root.dataset.roomAnalysisReady;
          this.requestRender();
        } catch (analysisError) {
          if (analysisSequence !== this.analysisSequence) return;
          this.automaticOcclusionMask = null;
          this.status.textContent = this.root.dataset.roomAnalysisError;
        }
      } catch (error) {
        this.status.textContent = this.root.dataset.photoError;
      } finally {
        this.setLoading(false);
      }
    }

    async createRoomAnalysisInput() {
      const imageWidth = this.photo.width || this.photo.naturalWidth;
      const imageHeight = this.photo.height || this.photo.naturalHeight;
      const scale = Math.min(1, ROOM_ANALYSIS_MAX_EDGE / Math.max(imageWidth, imageHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(imageWidth * scale));
      canvas.height = Math.max(1, Math.round(imageHeight * scale));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(this.photo, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(function (resolve, reject) {
        canvas.toBlob(function (result) {
          if (result) resolve(result);
          else reject(new Error('room-analysis-image-failed'));
        }, 'image/jpeg', 0.88);
      });
      return { blob, canvas };
    }

    async createAutomaticOcclusionMask() {
      const analysis = await this.createRoomAnalysisInput();
      const result = await estimateRoomDepth(analysis.blob);
      const depth = result.depth;
      if (!depth || !depth.data) throw new Error('room-depth-not-found');
      const width = depth.width;
      const height = depth.height;
      const channels = depth.channels || 1;
      let guideCanvas = analysis.canvas;
      if (guideCanvas.width !== width || guideCanvas.height !== height) {
        guideCanvas = document.createElement('canvas');
        guideCanvas.width = width;
        guideCanvas.height = height;
        const guideContext = guideCanvas.getContext('2d');
        guideContext.drawImage(analysis.canvas, 0, 0, width, height);
      }
      const guideContext = guideCanvas.getContext('2d', { willReadFrequently: true });
      const guide = guideContext.getImageData(0, 0, width, height).data;
      const rowDepth = new Float32Array(height);
      for (let y = 0; y < height; y += 1) {
        const samples = [];
        for (let x = Math.round(width * 0.05); x < width * 0.95; x += 3) {
          samples.push(depth.data[(y * width + x) * channels]);
        }
        samples.sort(function (a, b) {
          return a - b;
        });
        rowDepth[y] = samples[Math.floor(samples.length * 0.28)] || 0;
      }
      const floorDepth = new Float32Array(height);
      for (let y = 0; y < height; y += 1) {
        let sum = 0;
        let count = 0;
        for (let offset = -10; offset <= 10; offset += 1) {
          const row = Math.max(0, Math.min(height - 1, y + offset));
          sum += rowDepth[row];
          count += 1;
        }
        floorDepth[y] = sum / count;
      }
      const scores = new Float32Array(width * height);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixel = y * width + x;
          const difference = depth.data[pixel * channels] - floorDepth[y];
          scores[pixel] = y < height * 0.24 ? 0 : smoothstep(5.5, 12.5, difference);
        }
      }
      const refinedScores = refineOcclusionScores(scores, guide, width, height);
      const labels = removeMaskSpeckles(refinedScores, width, height);
      const floorBoundary = estimateFloorBoundary(floorDepth, guide, width);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      const image = context.createImageData(width, height);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixel = y * width + x;
          const offset = pixel * 4;
          const objectAlpha = labels[pixel]
            ? Math.round(255 * smoothstep(0.44, 0.56, refinedScores[pixel]))
            : 0;
          const nonFloorAlpha = Math.round(
            255 * (1 - smoothstep(floorBoundary - 2, floorBoundary + 4, y)),
          );
          const alpha = Math.max(objectAlpha, nonFloorAlpha);
          image.data[offset] = 255;
          image.data[offset + 1] = 255;
          image.data[offset + 2] = 255;
          image.data[offset + 3] = alpha;
        }
      }
      context.putImageData(image, 0, 0);
      canvas.floorBoundary = floorBoundary / height;
      return canvas;
    }

    loadLocalImage(file) {
      return new Promise(function (resolve, reject) {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = function () {
          URL.revokeObjectURL(url);
          resolve(image);
        };
        image.onerror = function () {
          URL.revokeObjectURL(url);
          reject(new Error('photo-load-failed'));
        };
        image.src = url;
      });
    }

    resize() {
      if (this.dialog.hidden) return;
      const rect = this.stage.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      if (width === this.viewport.width && height === this.viewport.height) return;
      const previous = this.viewport;
      this.viewport = { width, height };
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(width * pixelRatio);
      this.canvas.height = Math.round(height * pixelRatio);
      this.pixelRatio = pixelRatio;
      this.occlusionMaskCanvas.width = this.canvas.width;
      this.occlusionMaskCanvas.height = this.canvas.height;
      this.occlusionForegroundCanvas.width = this.canvas.width;
      this.occlusionForegroundCanvas.height = this.canvas.height;
      this.occlusionDirty = true;
      if (this.corners && previous.width && previous.height) {
        this.corners = this.corners.map(function (point) {
          return {
            x: (point.x / previous.width) * width,
            y: (point.y / previous.height) * height,
          };
        });
      }
      this.requestRender();
    }

    currentVariant() {
      return this.variants.find((variant) => String(variant.id) === String(this.sizeSelect.value));
    }

    currentRatio() {
      const variant = this.currentVariant();
      const dimensions = window.HeikoaDimensions.parseVariantDimensions(
        variant,
        this.data.optionNames,
      );
      return window.HeikoaDimensions.ratioForShape(dimensions, this.shape);
    }

    setLoading(loading) {
      const indicator = this.root.querySelector('[data-visualizer-loading]');
      indicator.hidden = !loading;
      this.stage.setAttribute('aria-busy', String(loading));
    }

    syncControlValues() {
      this.root.querySelectorAll('[data-visualizer-control]').forEach((control) => {
        control.value = String(Math.round(this[control.dataset.visualizerControl] * 100));
      });
    }

    snapshot() {
      return {
        corners: this.corners
          ? this.corners.map(function (point) {
              return { x: point.x, y: point.y };
            })
          : null,
        photoZoom: this.photoZoom,
        rugOpacity: this.rugOpacity,
        rugBrightness: this.rugBrightness,
        shadowStrength: this.shadowStrength,
        occlusionStrokes: this.occlusionStrokes.map(function (stroke) {
          return {
            action: stroke.action,
            radius: stroke.radius,
            points: stroke.points.map(function (point) {
              return { x: point.x, y: point.y };
            }),
          };
        }),
        variantId: String(this.sizeSelect.value),
      };
    }

    commitHistory() {
      if (!this.corners) return;
      const state = this.snapshot();
      const previous = this.history[this.historyIndex];
      if (previous && JSON.stringify(previous) === JSON.stringify(state)) return;
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(state);
      if (this.history.length > 30) this.history.shift();
      this.historyIndex = this.history.length - 1;
      this.updateHistoryButtons();
    }

    restoreHistoryState(state) {
      if (!state) return;
      this.corners = state.corners.map(function (point) {
        return { x: point.x, y: point.y };
      });
      this.photoZoom = state.photoZoom;
      this.rugOpacity = state.rugOpacity;
      this.rugBrightness = state.rugBrightness;
      this.shadowStrength = state.shadowStrength;
      this.occlusionStrokes = (state.occlusionStrokes || []).map(function (stroke) {
        return {
          action: stroke.action,
          radius: stroke.radius,
          points: stroke.points.map(function (point) {
            return { x: point.x, y: point.y };
          }),
        };
      });
      this.occlusionDirty = true;
      this.sizeSelect.value = state.variantId;
      this.constrainRugToFloor();
      this.syncControlValues();
      this.updateHistoryButtons();
      this.requestRender();
    }

    updateHistoryButtons() {
      this.root.querySelector('[data-visualizer-undo]').disabled = this.historyIndex <= 0;
      this.root.querySelector('[data-visualizer-redo]').disabled =
        this.historyIndex < 0 || this.historyIndex >= this.history.length - 1;
    }

    undo() {
      if (this.historyIndex <= 0) return;
      this.historyIndex -= 1;
      this.restoreHistoryState(this.history[this.historyIndex]);
    }

    redo() {
      if (this.historyIndex >= this.history.length - 1) return;
      this.historyIndex += 1;
      this.restoreHistoryState(this.history[this.historyIndex]);
    }

    reset(commit) {
      if (!this.viewport.width || !this.viewport.height) return;
      this.photoZoom = 1;
      this.rugOpacity = 1;
      this.rugBrightness = 1;
      this.shadowStrength = 0.65;
      this.occlusionStrokes = [];
      this.occlusionDirty = true;
      const ratio = Math.max(0.7, this.currentRatio());
      const width = Math.min(this.viewport.width * 0.58, 560);
      const height = Math.min(
        (width / ratio) * FLOOR_FORESHORTENING,
        this.viewport.height * 0.36,
      );
      const centerX = this.viewport.width / 2;
      const centerY = this.viewport.height * 0.72;
      const topWidth = width * 0.72;
      this.corners = [
        { x: centerX - topWidth / 2, y: centerY - height / 2 },
        { x: centerX + topWidth / 2, y: centerY - height / 2 },
        { x: centerX + width / 2, y: centerY + height / 2 },
        { x: centerX - width / 2, y: centerY + height / 2 },
      ];
      this.constrainRugToFloor();
      this.syncControlValues();
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    fitRugToFloor(normalizedBoundary) {
      if (!this.corners || !Number.isFinite(normalizedBoundary)) return;
      const layout = this.photoLayout();
      if (!layout) return;
      const floorY = layout.y + normalizedBoundary * layout.height + 6;
      const topY = (this.corners[0].y + this.corners[1].y) / 2;
      if (topY >= floorY) return;
      const bottomLimit = this.viewport.height - 12;
      const maximumY = Math.max.apply(
        null,
        this.corners.map(function (point) {
          return point.y;
        }),
      );
      const shift = Math.max(0, Math.min(floorY - topY, bottomLimit - maximumY));
      if (shift) {
        this.corners = this.corners.map(function (point) {
          return { x: point.x, y: point.y + shift };
        });
      }
      const shiftedTopY = topY + shift;
      if (shiftedTopY < floorY) {
        const bottomY = (this.corners[2].y + this.corners[3].y) / 2;
        const currentHeight = Math.max(1, bottomY - shiftedTopY);
        const scaleY = Math.max(0.35, Math.min(1, (bottomY - floorY) / currentHeight));
        this.corners = this.corners.map(function (point) {
          return { x: point.x, y: bottomY - (bottomY - point.y) * scaleY };
        });
      }
      this.commitHistory();
      this.requestRender();
    }

    floorBoundaryY() {
      if (!Number.isFinite(this.floorBoundary)) return null;
      const layout = this.photoLayout();
      return layout ? layout.y + this.floorBoundary * layout.height + 2 : null;
    }

    constrainRugToFloor() {
      const floorY = this.floorBoundaryY();
      if (!this.corners || floorY === null) return;
      const minimumY = Math.min.apply(
        null,
        this.corners.map(function (point) {
          return point.y;
        }),
      );
      if (minimumY >= floorY) return;
      const shift = floorY - minimumY;
      this.corners = this.corners.map(function (point) {
        return { x: point.x, y: point.y + shift };
      });
    }

    updateRatioForVariant(variantId, commit) {
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      const dimensions = window.HeikoaDimensions.parseVariantDimensions(
        variant,
        this.data.optionNames,
      );
      const ratio = window.HeikoaDimensions.ratioForShape(dimensions, this.shape);
      if (!this.corners || !ratio) return;
      const topWidth = distance(this.corners[0], this.corners[1]);
      const bottomWidth = distance(this.corners[3], this.corners[2]);
      const leftHeight = distance(this.corners[0], this.corners[3]);
      const rightHeight = distance(this.corners[1], this.corners[2]);
      const currentWidth = (topWidth + bottomWidth) / 2;
      const currentHeight = Math.max(1, (leftHeight + rightHeight) / 2);
      const scaleY = Math.max(
        0.35,
        Math.min(2.8, ((currentWidth / ratio) * FLOOR_FORESHORTENING) / currentHeight),
      );
      const center = centerOf(this.corners);
      const angle = Math.atan2(
        this.corners[1].y - this.corners[0].y,
        this.corners[1].x - this.corners[0].x,
      );
      const vertical = { x: -Math.sin(angle), y: Math.cos(angle) };
      this.corners = this.corners.map(function (point) {
        const relative = { x: point.x - center.x, y: point.y - center.y };
        const verticalDistance = relative.x * vertical.x + relative.y * vertical.y;
        return {
          x: point.x + vertical.x * verticalDistance * (scaleY - 1),
          y: point.y + vertical.y * verticalDistance * (scaleY - 1),
        };
      });
      this.constrainRugToFloor();
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    rotate(degrees, commit) {
      if (!this.corners) return;
      const center = centerOf(this.corners);
      const radians = (degrees * Math.PI) / 180;
      const cosine = Math.cos(radians);
      const sine = Math.sin(radians);
      this.corners = this.corners.map(function (point) {
        const x = point.x - center.x;
        const y = point.y - center.y;
        return {
          x: center.x + x * cosine - y * sine,
          y: center.y + x * sine + y * cosine,
        };
      });
      this.constrainRugToFloor();
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    scale(factor, commit) {
      if (!this.corners) return;
      const center = centerOf(this.corners);
      this.corners = this.corners.map(function (point) {
        return {
          x: center.x + (point.x - center.x) * factor,
          y: center.y + (point.y - center.y) * factor,
        };
      });
      this.constrainRugToFloor();
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    eventPoint(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    photoLayout() {
      if (!this.photo) return null;
      const imageWidth = this.photo.width || this.photo.naturalWidth;
      const imageHeight = this.photo.height || this.photo.naturalHeight;
      const scale =
        Math.max(this.viewport.width / imageWidth, this.viewport.height / imageHeight) * this.photoZoom;
      const width = imageWidth * scale;
      const height = imageHeight * scale;
      return {
        x: (this.viewport.width - width) / 2,
        y: (this.viewport.height - height) / 2,
        width,
        height,
      };
    }

    stagePointToPhoto(point) {
      const layout = this.photoLayout();
      if (!layout) return null;
      return {
        x: (point.x - layout.x) / layout.width,
        y: (point.y - layout.y) / layout.height,
      };
    }

    photoPointToStage(point, layout) {
      return {
        x: layout.x + point.x * layout.width,
        y: layout.y + point.y * layout.height,
      };
    }

    setOcclusionMode(active) {
      if (active && !this.photo) return;
      this.occlusionMode = Boolean(active);
      this.brushPoint = null;
      this.pointers.clear();
      this.drag = null;
      this.gesture = null;
      this.occlusionToggle.setAttribute('aria-pressed', String(this.occlusionMode));
      this.occlusionTools.hidden = !this.occlusionMode;
      this.stage.classList.toggle('is-occlusion-editing', this.occlusionMode);
      this.requestRender();
      if (this.occlusionMode) this.setOcclusionAction('hide');
    }

    setOcclusionAction(action) {
      this.occlusionAction = action === 'restore' ? 'restore' : 'hide';
      this.root.querySelectorAll('[data-visualizer-occlusion-action]').forEach((button) => {
        const selected = button.dataset.visualizerOcclusionAction === this.occlusionAction;
        button.setAttribute('aria-pressed', String(selected));
      });
      this.requestRender();
    }

    nearestHandle(point) {
      if (!this.corners) return -1;
      let match = -1;
      let nearest = HANDLE_RADIUS;
      this.corners.forEach(function (corner, index) {
        const candidate = distance(point, corner);
        if (candidate < nearest) {
          nearest = candidate;
          match = index;
        }
      });
      return match;
    }

    pointerDown(event) {
      if (!this.photo || !this.corners) return;
      event.preventDefault();
      this.interactionChanged = false;
      this.canvas.setPointerCapture(event.pointerId);
      const point = this.eventPoint(event);
      this.brushPoint = point;
      if (this.occlusionMode) {
        if (this.pointers.size) return;
        const normalizedPoint = this.stagePointToPhoto(point);
        const layout = this.photoLayout();
        if (!normalizedPoint || !layout) return;
        const stroke = {
          action: this.occlusionAction,
          radius: this.brushRadius / Math.min(layout.width, layout.height),
          points: [normalizedPoint],
        };
        this.occlusionStrokes.push(stroke);
        this.occlusionDirty = true;
        this.pointers.set(event.pointerId, point);
        this.drag = { type: 'occlusion', stroke, last: point };
        this.interactionChanged = true;
        this.requestRender();
        return;
      }
      this.pointers.set(event.pointerId, point);
      if (this.pointers.size === 2) {
        this.beginGesture();
        return;
      }
      const handle = this.nearestHandle(point);
      if (handle >= 0) this.drag = { type: 'corner', handle, last: point };
      else if (pointInPolygon(point, this.corners)) this.drag = { type: 'move', last: point };
    }

    beginGesture() {
      const entries = Array.from(this.pointers.entries()).slice(0, 2);
      this.gesture = {
        ids: entries.map(function (entry) {
          return entry[0];
        }),
        points: entries.map(function (entry) {
          return { x: entry[1].x, y: entry[1].y };
        }),
        corners: this.corners.map(function (point) {
          return { x: point.x, y: point.y };
        }),
      };
      this.drag = null;
    }

    pointerMove(event) {
      const hoverPoint = this.eventPoint(event);
      if (this.occlusionMode) {
        this.brushPoint = hoverPoint;
        this.requestRender();
      }
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const point = hoverPoint;
      this.pointers.set(event.pointerId, point);
      if (this.drag && this.drag.type === 'occlusion') {
        if (distance(point, this.drag.last) < 2) return;
        const normalizedPoint = this.stagePointToPhoto(point);
        if (!normalizedPoint) return;
        this.drag.stroke.points.push(normalizedPoint);
        this.occlusionDirty = true;
        this.drag.last = point;
        this.interactionChanged = true;
        this.requestRender();
        return;
      }
      if (this.gesture && this.pointers.size >= 2) {
        const current = this.gesture.ids.map((id) => this.pointers.get(id));
        if (current.some((item) => !item)) return;
        const baseCenter = centerOf(this.gesture.points);
        const currentCenter = centerOf(current);
        const baseDistance = Math.max(1, distance(this.gesture.points[0], this.gesture.points[1]));
        const scale = distance(current[0], current[1]) / baseDistance;
        const baseAngle = Math.atan2(
          this.gesture.points[1].y - this.gesture.points[0].y,
          this.gesture.points[1].x - this.gesture.points[0].x,
        );
        const currentAngle = Math.atan2(current[1].y - current[0].y, current[1].x - current[0].x);
        const angle = currentAngle - baseAngle;
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        this.corners = this.gesture.corners.map(function (corner) {
          const x = (corner.x - baseCenter.x) * scale;
          const y = (corner.y - baseCenter.y) * scale;
          return {
            x: currentCenter.x + x * cosine - y * sine,
            y: currentCenter.y + x * sine + y * cosine,
          };
        });
        this.constrainRugToFloor();
        this.interactionChanged = true;
        this.requestRender();
        return;
      }
      if (!this.drag) return;
      if (this.drag.type === 'corner') {
        const floorY = this.floorBoundaryY();
        this.corners[this.drag.handle] = {
          x: point.x,
          y: floorY === null ? point.y : Math.max(point.y, floorY),
        };
      } else {
        const delta = { x: point.x - this.drag.last.x, y: point.y - this.drag.last.y };
        this.corners = this.corners.map(function (corner) {
          return { x: corner.x + delta.x, y: corner.y + delta.y };
        });
        this.constrainRugToFloor();
      }
      this.drag.last = point;
      this.interactionChanged = true;
      this.requestRender();
    }

    pointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.gesture = null;
      this.drag = null;
      if (this.interactionChanged && this.pointers.size === 0) {
        this.commitHistory();
        this.interactionChanged = false;
      }
    }

    requestRender() {
      if (this.frameRequested) return;
      this.frameRequested = true;
      requestAnimationFrame(() => {
        this.frameRequested = false;
        this.render();
      });
    }

    render() {
      const context = this.context;
      const width = this.viewport.width;
      const height = this.viewport.height;
      if (!width || !height) return;
      context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);
      if (this.photo) this.drawPhoto(context, width, height);
      else {
        const gradient = context.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#eef0ec');
        gradient.addColorStop(1, '#cbd2ce');
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
      }
      if (!this.photo || !this.rugTexture || !this.corners) return;
      this.drawRug(context);
      this.drawOcclusionForeground(context, width, height);
      if (this.occlusionMode) this.drawBrushIndicator(context);
    }

    drawPhoto(context, width, height) {
      const layout = this.photoLayout();
      if (!layout) return;
      context.drawImage(this.photo, layout.x, layout.y, layout.width, layout.height);
    }

    drawRug(context) {
      const homography = homographyFor(this.corners);
      if (!homography) return;
      context.save();
      context.filter = 'blur(5px)';
      context.fillStyle = `rgba(0, 0, 0, ${0.16 * this.shadowStrength})`;
      context.beginPath();
      this.corners.forEach(function (point, index) {
        const x = point.x;
        const y = point.y + 2;
        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      });
      context.closePath();
      context.fill();
      context.restore();

      const imageWidth = this.rugTexture.width || this.rugTexture.naturalWidth;
      const imageHeight = this.rugTexture.height || this.rugTexture.naturalHeight;
      context.save();
      context.globalAlpha = this.rugOpacity;
      context.filter = `brightness(${this.rugBrightness}) saturate(0.94) contrast(0.97)`;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      for (let row = 0; row < MESH_SIZE; row += 1) {
        for (let column = 0; column < MESH_SIZE; column += 1) {
          const u0 = column / MESH_SIZE;
          const u1 = (column + 1) / MESH_SIZE;
          const v0 = row / MESH_SIZE;
          const v1 = (row + 1) / MESH_SIZE;
          const sourceTopLeft = { x: u0 * imageWidth, y: v0 * imageHeight };
          const sourceTopRight = { x: u1 * imageWidth, y: v0 * imageHeight };
          const sourceBottomRight = { x: u1 * imageWidth, y: v1 * imageHeight };
          const sourceBottomLeft = { x: u0 * imageWidth, y: v1 * imageHeight };
          const topLeft = projectPoint(homography, u0, v0);
          const topRight = projectPoint(homography, u1, v0);
          const bottomRight = projectPoint(homography, u1, v1);
          const bottomLeft = projectPoint(homography, u0, v1);
          drawTriangle(
            context,
            this.rugTexture,
            [sourceTopLeft, sourceTopRight, sourceBottomRight],
            [topLeft, topRight, bottomRight],
          );
          drawTriangle(
            context,
            this.rugTexture,
            [sourceTopLeft, sourceBottomRight, sourceBottomLeft],
            [topLeft, bottomRight, bottomLeft],
          );
        }
      }
      context.restore();
    }

    drawOcclusionForeground(context, width, height) {
      if (!this.automaticOcclusionMask && !this.occlusionStrokes.length) return;
      const layout = this.photoLayout();
      if (!layout) return;
      const foreground = this.occlusionForegroundCanvas;
      if (this.occlusionDirty) {
        const mask = this.occlusionMaskCanvas;
        const maskContext = mask.getContext('2d');
        maskContext.setTransform(1, 0, 0, 1, 0, 0);
        maskContext.clearRect(0, 0, mask.width, mask.height);
        maskContext.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
        if (this.automaticOcclusionMask) {
          maskContext.imageSmoothingEnabled = true;
          maskContext.imageSmoothingQuality = 'high';
          maskContext.drawImage(this.automaticOcclusionMask, layout.x, layout.y, layout.width, layout.height);
        }
        maskContext.lineCap = 'round';
        maskContext.lineJoin = 'round';
        this.occlusionStrokes.forEach((stroke) => {
          const points = stroke.points.map((point) => this.photoPointToStage(point, layout));
          if (!points.length) return;
          maskContext.globalCompositeOperation =
            stroke.action === 'restore' ? 'destination-out' : 'source-over';
          maskContext.strokeStyle = '#000';
          maskContext.fillStyle = '#000';
          maskContext.lineWidth = stroke.radius * Math.min(layout.width, layout.height) * 2;
          if (points.length === 1) {
            maskContext.beginPath();
            maskContext.arc(points[0].x, points[0].y, maskContext.lineWidth / 2, 0, Math.PI * 2);
            maskContext.fill();
            return;
          }
          maskContext.beginPath();
          maskContext.moveTo(points[0].x, points[0].y);
          points.slice(1).forEach(function (point) {
            maskContext.lineTo(point.x, point.y);
          });
          maskContext.stroke();
        });
        maskContext.globalCompositeOperation = 'source-over';

        const foregroundContext = foreground.getContext('2d');
        foregroundContext.setTransform(1, 0, 0, 1, 0, 0);
        foregroundContext.clearRect(0, 0, foreground.width, foreground.height);
        foregroundContext.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
        this.drawPhoto(foregroundContext, width, height);
        foregroundContext.globalCompositeOperation = 'destination-in';
        foregroundContext.setTransform(1, 0, 0, 1, 0, 0);
        foregroundContext.drawImage(mask, 0, 0);
        foregroundContext.globalCompositeOperation = 'source-over';
        this.occlusionDirty = false;
      }

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.drawImage(foreground, 0, 0);
      context.restore();
    }

    drawBrushIndicator(context) {
      if (!this.brushPoint) return;
      context.save();
      context.beginPath();
      context.arc(this.brushPoint.x, this.brushPoint.y, this.brushRadius, 0, Math.PI * 2);
      context.fillStyle = this.occlusionAction === 'restore' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(31, 100, 85, 0.2)';
      context.fill();
      context.strokeStyle = this.occlusionAction === 'restore' ? '#ffffff' : '#1f6455';
      context.lineWidth = 2;
      context.stroke();
      context.restore();
    }

    selectVariant(variantId, reshape) {
      const option = Array.from(this.sizeSelect.options).find(function (item) {
        return String(item.value) === String(variantId);
      });
      if (!option) return;
      this.sizeSelect.value = String(variantId);
      this.selectedVariantId = String(variantId);
      if (reshape !== false) this.updateRatioForVariant(variantId);
    }

    emit(eventName) {
      this.root.dispatchEvent(
        new CustomEvent('heikoa:visualizer', {
          bubbles: true,
          detail: {
            event: eventName,
            productId: this.root.dataset.productId || '',
            variantId: String(this.sizeSelect.value || ''),
            shape: this.shape,
            context: this.root.dataset.productUrl ? 'dedicated_page' : 'product_page',
          },
        }),
      );
    }

    confirm() {
      const variant = this.currentVariant();
      if (!variant) return;
      this.emit('confirmed');
      if (this.root.dataset.addToCart === 'true') {
        const form = this.root.querySelector('[data-visualizer-cart-form]');
        const variantInput = this.root.querySelector('[data-visualizer-cart-variant]');
        if (!form || !variantInput) return;
        this.selectedVariantId = String(variant.id);
        variantInput.value = String(variant.id);
        this.status.textContent = this.root.dataset.cartSubmitting;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
        return;
      }
      if (this.root.dataset.productUrl) {
        this.selectedVariantId = String(variant.id);
        this.status.textContent = this.root.dataset.selectionConfirmed;
        window.setTimeout(() => {
          window.location.assign(`${this.root.dataset.productUrl}?variant=${encodeURIComponent(variant.id)}`);
        }, 420);
        return;
      }
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || document;
      const radios = section.querySelector(`variant-radios[data-section="${this.sectionId}"]`);
      const selects = section.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (radios) {
        const fieldsets = radios.querySelectorAll('fieldset');
        variant.options.forEach(function (value, index) {
          const input = Array.from(fieldsets[index]?.querySelectorAll('input[type="radio"]') || []).find(
            function (candidate) {
              return candidate.value === value;
            },
          );
          if (input) input.checked = true;
        });
        radios.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (selects) {
        const controls = selects.querySelectorAll('select');
        variant.options.forEach(function (value, index) {
          if (controls[index]) controls[index].value = value;
        });
        selects.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const input = section.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
        if (input) {
          input.value = String(variant.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.selectedVariantId = String(variant.id);
      this.status.textContent = this.root.dataset.selectionConfirmed;
      window.setTimeout(() => this.close(), 420);
    }
  }

  function initialize(root) {
    if (root.dataset.visualizerReady === 'true') return;
    root.dataset.visualizerReady = 'true';
    new RugVisualizer(root);
  }

  function initializeAll(scope) {
    scope.querySelectorAll('[data-heikoa-visualizer]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
